import React from "react";
import { useApiQuery } from "@/lib/useApiQuery";
import {
  Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { PiggyBank, TrendingUp } from "lucide-react";
import { AXIS, LABEL_STYLE, SMALL_CHART_MARGIN, TOOLTIP_STYLE } from "@/lib/chartStyles";

const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

function SavingsChart({ months }) {
  if (months.length < 2) return null;
  return (
    <div className="mt-6 h-44" data-testid="fault-savings-chart">
      <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
        <AreaChart data={months} margin={SMALL_CHART_MARGIN}>
          <defs>
            <linearGradient id="savings-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#156F5B" stopOpacity={0.35} />
              <stop offset="100%" stopColor="#156F5B" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#F0F2F1" vertical={false} />
          <XAxis dataKey="label" tick={AXIS} interval="preserveStartEnd" minTickGap={30} />
          <YAxis tick={AXIS} width={56} tickFormatter={money} />
          <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={LABEL_STYLE} formatter={(v) => money(v)} />
          <Area type="monotone" dataKey="cumulative" name="Saved to date" stroke="#156F5B" strokeWidth={2} fill="url(#savings-fill)" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function FixRow({ fix }) {
  return (
    <div className="py-2.5 flex items-center justify-between gap-4" data-testid={`savings-fix-${fix.task_id}`}>
      <div className="min-w-0">
        <div className="text-sm font-heading font-semibold text-[#212121] truncate">{fix.rule}</div>
        <div className="k-sublabel mt-0.5">
          {fix.site_dis} · fixed {new Date(fix.fixed_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
          {fix.still_active && " · still reporting"}
        </div>
      </div>
      <div className="text-right shrink-0">
        <div className="text-sm font-heading font-bold text-[#156F5B]">{money(fix.saved_to_date)}</div>
        <div className="k-sublabel">{money(fix.weekly_impact)}/week removed</div>
      </div>
    </div>
  );
}

/** Running total of money saved since your crews started fixing faults. */
export function FaultSavings() {
  const { data } = useApiQuery(["faultSavings"], "/faults/savings");

  if (!data || !data.fix_count) return null;

  return (
    <div className="mt-6 k-card p-6" data-testid="fault-savings">
      <div className="flex items-start justify-between flex-wrap gap-6">
        <div>
          <div className="k-label flex items-center gap-2">
            <PiggyBank className="h-3.5 w-3.5" /> Savings so far
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-5xl font-heading font-bold text-[#156F5B]" data-testid="savings-total">
              {money(data.total_saved)}
            </span>
            <span className="text-xs text-[#6B7280] font-heading">and counting</span>
          </div>
          <p className="mt-2 text-sm text-[#4D4D4D]">
            From {data.fix_count} fault{data.fix_count === 1 ? "" : "s"} your team has fixed since{" "}
            {new Date(`${data.since}T00:00:00`).toLocaleDateString(undefined, { month: "long", year: "numeric" })}.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-6 text-right">
          <div>
            <div className="k-sublabel">This month</div>
            <div className="mt-1 text-lg font-heading font-semibold text-[#212121]" data-testid="savings-this-month">
              {money(data.this_month)}
            </div>
          </div>
          <div>
            <div className="k-sublabel">This year</div>
            <div className="mt-1 text-lg font-heading font-semibold text-[#212121]" data-testid="savings-this-year">
              {money(data.this_year)}
            </div>
          </div>
          <div>
            <div className="k-sublabel">Weekly run rate</div>
            <div className="mt-1 text-lg font-heading font-semibold text-[#212121] inline-flex items-center gap-1" data-testid="savings-run-rate">
              <TrendingUp className="h-3.5 w-3.5 text-[#156F5B]" />{money(data.weekly_run_rate)}
            </div>
          </div>
        </div>
      </div>

      <SavingsChart months={data.months || []} />

      {(data.fixes || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-[#F0F2F1] divide-y divide-[#F0F2F1]" data-testid="savings-fixes">
          {data.fixes.map((f) => <FixRow key={f.task_id} fix={f} />)}
        </div>
      )}

      <p className="mt-4 text-xs text-[#9CA3AF]">
        Estimated from the weekly cost each fault was wasting when it was claimed, less anything the
        same rule still costs, accrued for every week since the fix.
      </p>
    </div>
  );
}
